/**
 * AdminUserDetail Component
 * Full detail page for viewing and editing a user
 * URL: /beu-admin-x7k9/users/:userId
 */

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';
import './Admin.css';

class AdminUserDetail extends Component {
    constructor(props) {
        super(props);
        this.state = {
            user: null,
            loading: true,
            error: null,
            isEditing: false,
            editForm: {},
            saving: false,
            saveSuccess: false,
            saveError: null
        };
    }

    componentDidMount() {
        this.fetchUser();
    }

    fetchUser = async () => {
        const { id } = this.props.match.params;
        this.setState({ loading: true, error: null });

        try {
            const response = await adminService.getUserById(id);
            if (response.statusCode === 1) {
                const user = response.responseData;
                this.setState({
                    user,
                    editForm: {
                        name: user.name || '',
                        email: user.email || '',
                        phoneNumber: user.phoneNumber || '',
                        isAdmin: user.isAdmin || false,
                        isActive: user.isActive !== false
                    },
                    loading: false
                });
            } else {
                this.setState({ error: 'Failed to load user', loading: false });
            }
        } catch (error) {
            console.error('Error fetching user:', error);
            this.setState({ error: 'Failed to load user', loading: false });
        }
    }

    handleEditFormChange = (field, value) => {
        this.setState(prevState => ({
            editForm: {
                ...prevState.editForm,
                [field]: value
            },
            saveSuccess: false,
            saveError: null
        }));
    }

    handleSave = async () => {
        const { user, editForm } = this.state;
        this.setState({ saving: true, saveError: null, saveSuccess: false });

        try {
            const response = await adminService.updateUser(user._id, editForm);
            if (response.statusCode === 1) {
                this.setState({
                    user: response.responseData,
                    saving: false,
                    isEditing: false,
                    saveSuccess: true
                });
                // Clear success message after 3 seconds
                setTimeout(() => this.setState({ saveSuccess: false }), 3000);
            } else {
                this.setState({ saveError: 'Failed to save changes', saving: false });
            }
        } catch (error) {
            console.error('Error saving user:', error);
            this.setState({ saveError: 'Failed to save changes', saving: false });
        }
    }

    handleDelete = async () => {
        const { user } = this.state;
        if (!window.confirm(`Are you sure you want to delete "${user.name || user.email}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await adminService.deleteUser(user._id);
            if (response.statusCode === 1) {
                this.props.history.push('/beu-admin-x7k9/users');
            } else {
                alert('Failed to delete user');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Failed to delete user');
        }
    }

    cancelEdit = () => {
        const { user } = this.state;
        this.setState({
            isEditing: false,
            editForm: {
                name: user.name || '',
                email: user.email || '',
                phoneNumber: user.phoneNumber || '',
                isAdmin: user.isAdmin || false,
                isActive: user.isActive !== false
            },
            saveError: null
        });
    }

    getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }

    formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    render() {
        const { user, loading, error, isEditing, editForm, saving, saveSuccess, saveError } = this.state;

        if (loading) {
            return (
                <AdminWrapper>
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                        <div className="admin-loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                        <p style={{ color: '#666' }}>Loading user...</p>
                    </div>
                </AdminWrapper>
            );
        }

        if (error || !user) {
            return (
                <AdminWrapper>
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                        <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error || 'User not found'}</p>
                        <Link to="/beu-admin-x7k9/users" className="admin-btn admin-btn-secondary">
                            Back to Users
                        </Link>
                    </div>
                </AdminWrapper>
            );
        }

        return (
            <AdminWrapper>
                {/* Breadcrumb */}
                <div style={{ marginBottom: '24px' }}>
                    <Link to="/beu-admin-x7k9/users" style={{ color: '#666', textDecoration: 'none', fontSize: '14px' }}>
                        ← Back to Users
                    </Link>
                </div>

                {/* Success/Error Messages */}
                {saveSuccess && (
                    <div style={{
                        padding: '12px 16px',
                        background: '#e8f5e9',
                        borderRadius: '8px',
                        marginBottom: '24px',
                        color: '#2e7d32',
                        fontWeight: '500'
                    }}>
                        ✓ Changes saved successfully
                    </div>
                )}
                {saveError && (
                    <div style={{
                        padding: '12px 16px',
                        background: '#ffebee',
                        borderRadius: '8px',
                        marginBottom: '24px',
                        color: '#c62828',
                        fontWeight: '500'
                    }}>
                        ✕ {saveError}
                    </div>
                )}

                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: '32px',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        {user.localAvatar || user.avatar ? (
                            <img
                                src={user.localAvatar || user.avatar}
                                alt={user.name}
                                style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }}
                            />
                        ) : (
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #1a1a1a 0%, #333 100%)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '28px',
                                fontWeight: '600'
                            }}>
                                {this.getInitials(user.name)}
                            </div>
                        )}
                        <div>
                            <h1 style={{ margin: '0 0 4px 0', fontSize: '28px', fontWeight: '700' }}>
                                {user.name || 'Unknown User'}
                            </h1>
                            <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>{user.email}</p>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <span className={`admin-badge ${user.isActive !== false ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                                    {user.isActive !== false ? 'Active' : 'Inactive'}
                                </span>
                                {user.isAdmin && (
                                    <span className="admin-badge admin-badge-info">Admin</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        {!isEditing ? (
                            <button
                                className="admin-btn admin-btn-primary"
                                onClick={() => this.setState({ isEditing: true })}
                            >
                                Edit User
                            </button>
                        ) : (
                            <>
                                <button
                                    className="admin-btn admin-btn-secondary"
                                    onClick={this.cancelEdit}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="admin-btn admin-btn-primary"
                                    onClick={this.handleSave}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Content Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
                    {/* Main Content */}
                    <div>
                        {/* User Information */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">User Information</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                {isEditing ? (
                                    <div className="admin-form">
                                        <div className="admin-form-group">
                                            <label>Full Name</label>
                                            <input
                                                type="text"
                                                value={editForm.name}
                                                onChange={(e) => this.handleEditFormChange('name', e.target.value)}
                                                placeholder="Enter full name"
                                            />
                                        </div>
                                        <div className="admin-form-group">
                                            <label>Email Address</label>
                                            <input
                                                type="email"
                                                value={editForm.email}
                                                onChange={(e) => this.handleEditFormChange('email', e.target.value)}
                                                placeholder="Enter email address"
                                            />
                                        </div>
                                        <div className="admin-form-group">
                                            <label>Phone Number</label>
                                            <input
                                                type="tel"
                                                value={editForm.phoneNumber}
                                                onChange={(e) => this.handleEditFormChange('phoneNumber', e.target.value)}
                                                placeholder="Enter phone number"
                                            />
                                        </div>
                                        <div className="admin-form-row">
                                            <div className="admin-form-group">
                                                <label className="admin-checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={editForm.isActive}
                                                        onChange={(e) => this.handleEditFormChange('isActive', e.target.checked)}
                                                    />
                                                    <span>Account Active</span>
                                                </label>
                                            </div>
                                            <div className="admin-form-group">
                                                <label className="admin-checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={editForm.isAdmin}
                                                        onChange={(e) => this.handleEditFormChange('isAdmin', e.target.checked)}
                                                    />
                                                    <span>Admin Access</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="admin-detail-grid">
                                        <div className="admin-detail-row">
                                            <label>Full Name</label>
                                            <span>{user.name || 'N/A'}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Email Address</label>
                                            <span>{user.email || 'N/A'}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Phone Number</label>
                                            <span>{user.phoneNumber || 'N/A'}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Account Status</label>
                                            <span className={`admin-badge ${user.isActive !== false ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                                                {user.isActive !== false ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Admin Access</label>
                                            <span className={`admin-badge ${user.isAdmin ? 'admin-badge-info' : 'admin-badge-secondary'}`}>
                                                {user.isAdmin ? 'Yes' : 'No'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Associated Professional Profile */}
                        {user.hairdresser && (
                            <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                                <div className="admin-table-header">
                                    <h3 className="admin-table-title">Professional Profile</h3>
                                </div>
                                <div style={{ padding: '24px' }}>
                                    <p style={{ color: '#666', marginBottom: '16px' }}>
                                        This user has an associated professional profile.
                                    </p>
                                    <Link
                                        to={`/beu-admin-x7k9/professionals/${user.hairdresser._id || user.hairdresser}`}
                                        className="admin-btn admin-btn-secondary"
                                    >
                                        View Professional Profile →
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div>
                        {/* System Info */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">System Information</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div className="admin-detail-grid">
                                    <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                        <label>User ID</label>
                                        <span className="admin-id-text">{user._id}</span>
                                    </div>
                                    <div className="admin-detail-row">
                                        <label>Created</label>
                                        <span>{this.formatDateTime(user.createdTime || user.createdAt)}</span>
                                    </div>
                                    <div className="admin-detail-row">
                                        <label>Last Updated</label>
                                        <span>{this.formatDateTime(user.updatedTime || user.updatedAt)}</span>
                                    </div>
                                    {user.country && (
                                        <div className="admin-detail-row">
                                            <label>Country</label>
                                            <span>
                                                {typeof user.country === 'object'
                                                    ? user.country.englishName || user.country.name
                                                    : user.country}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="admin-table-container">
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Quick Actions</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <button
                                        className="admin-btn admin-btn-secondary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                        onClick={() => {
                                            const newStatus = !(user.isActive !== false);
                                            this.handleEditFormChange('isActive', newStatus);
                                            this.setState({ isEditing: false }, this.handleSave);
                                        }}
                                    >
                                        {user.isActive !== false ? 'Deactivate Account' : 'Activate Account'}
                                    </button>
                                    <button
                                        className="admin-btn admin-btn-secondary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                        onClick={() => {
                                            const newAdmin = !user.isAdmin;
                                            this.handleEditFormChange('isAdmin', newAdmin);
                                            this.setState({ isEditing: false }, this.handleSave);
                                        }}
                                    >
                                        {user.isAdmin ? 'Remove Admin Access' : 'Grant Admin Access'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Danger Zone */}
                <div className="admin-danger-zone" style={{ marginTop: '48px' }}>
                    <div className="admin-table-container" style={{ border: '1px solid #ff4444' }}>
                        <div className="admin-table-header" style={{ background: '#fff5f5' }}>
                            <h3 className="admin-table-title" style={{ color: '#cc0000' }}>Danger Zone</h3>
                        </div>
                        <div style={{ padding: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                                <div>
                                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>Delete this user</div>
                                    <div style={{ color: '#666', fontSize: '14px' }}>
                                        Once deleted, this user's account and all associated data will be permanently removed.
                                    </div>
                                </div>
                                <button
                                    className="admin-btn admin-btn-danger"
                                    onClick={this.handleDelete}
                                >
                                    Delete User
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </AdminWrapper>
        );
    }
}

export default AdminUserDetail;
